/**
 * Businesses Directory – content below header/search. Data from APIs only.
 * Sections: Filter strip, Featured, Listings (with filter row), CTA.
 * Tailwind only, mobile-first.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import { businessService } from '../services/api';

function getImageUrl(b) {
  if (!b) return null;
  if (b.display_image?.original_image) return b.display_image.original_image;
  if (b.images?.length > 0 && b.images[0].original_image) return b.images[0].original_image;
  return null;
}

function getCity(b) {
  return b?.society?.city || '';
}

function getCategoryLabel(category) {
  if (!category) return 'Other';
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function StarRating({ rating, reviewsCount }) {
  if (rating == null || rating === 0) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  const r = Number(rating);
  const whole = Math.floor(r);
  const hasHalf = r % 1 >= 0.5;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <div className="flex text-amber-400 text-sm">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i}>{i <= whole ? '★' : i === whole + 1 && hasHalf ? '½' : '☆'}</span>
        ))}
      </div>
      <span className="text-gray-600 text-sm">{r.toFixed(1)}</span>
      {reviewsCount != null && reviewsCount > 0 && (
        <span className="text-gray-400 text-sm">• {reviewsCount}</span>
      )}
    </div>
  );
}

function BusinessCardFeatured({ business, onViewDetails }) {
  const imageUrl = getImageUrl(business);
  const city = getCity(business);
  const category = getCategoryLabel(business.category);

  return (
    <div className="flex-shrink-0 w-[280px] sm:w-[300px] bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative aspect-video overflow-hidden group bg-gray-100">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-1">{business.name}</h3>
        <p className="text-sm text-gray-500 mt-0.5">{category} • {city}</p>
        <div className="mt-2">
          <StarRating rating={business.rating} reviewsCount={business.total_ratings ?? business.views} />
        </div>
        <button
          type="button"
          onClick={() => onViewDetails(business)}
          className="mt-3 w-full py-2 rounded-lg text-sm font-medium text-emerald-600 border border-emerald-500 hover:bg-emerald-50 transition-colors"
        >
          View Details →
        </button>
      </div>
    </div>
  );
}

function BusinessCardSmall({ business, onViewDetails }) {
  const imageUrl = getImageUrl(business);
  const city = getCity(business);
  const category = getCategoryLabel(business.category);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative aspect-video overflow-hidden group bg-gray-100">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{business.name}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{category} • {city}</p>
        <div className="mt-1 flex items-center gap-1 text-amber-400 text-xs">
          {business.rating != null && business.rating > 0 ? `${Number(business.rating).toFixed(1)} ★` : `${business.views || 0} views`}
        </div>
        <button type="button" onClick={() => onViewDetails(business)} className="mt-2 w-full py-1.5 rounded-lg text-xs font-medium text-emerald-600 border border-emerald-500 hover:bg-emerald-50">View Details</button>
      </div>
    </div>
  );
}

export default function BusinessesPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [areaFilter, setAreaFilter] = useState('All Areas');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [areaListings, setAreaListings] = useState('All Areas');
  const [sortBy, setSortBy] = useState('Most Popular');

  const [featured, setFeatured] = useState([]);
  const [listings, setListings] = useState([]);
  const [listingsCount, setListingsCount] = useState(0);
  const [listingsNextPage, setListingsNextPage] = useState(null);
  const [listingsPage, setListingsPage] = useState(1);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingMoreListings, setLoadingMoreListings] = useState(false);
  const [listingsError, setListingsError] = useState(null);

  const LISTINGS_PAGE_SIZE = 24;

  const handleViewDetails = (business) => {
    if (business?.slug) navigate(`/businesses/${business.slug}`);
    else if (business?.id) navigate(`/businesses/${business.id}`);
  };

  const parseListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.results) return response.results;
    if (response?.data?.results) return response.data.results;
    return [];
  };

  const fetchFeatured = useCallback(async () => {
    setLoadingFeatured(true);
    try {
      const res = await businessService.getBusinesses({
        is_featured: true,
        is_public: true,
        page_size: 5,
      });
      setFeatured(parseListResponse(res));
    } catch (err) {
      setFeatured([]);
    } finally {
      setLoadingFeatured(false);
    }
  }, []);

  const fetchListings = useCallback(async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingListings(true);
    } else {
      setLoadingMoreListings(true);
    }
    setListingsError(null);
    try {
      const params = {
        page,
        page_size: LISTINGS_PAGE_SIZE,
        search: searchInput.trim() || undefined,
        category: categoryFilter === 'All Categories' ? undefined : categoryFilter,
        city: areaListings === 'All Areas' ? undefined : areaListings,
        ordering: sortBy === 'Newest' ? '-created_at' : sortBy === 'Name' ? 'name' : '-views',
      };
      const res = await businessService.getBusinesses(params);
      const items = parseListResponse(res);
      const count = res?.count ?? items?.length ?? 0;
      const next = res?.next ?? null;
      if (append && page > 1) {
        setListings((prev) => [...prev, ...items]);
      } else {
        setListings(items);
      }
      setListingsCount(count);
      setListingsNextPage(next);
      setListingsPage(page);
    } catch (err) {
      setListingsError('Failed to load businesses. Please try again.');
      if (!append) setListings([]);
    } finally {
      setLoadingListings(false);
      setLoadingMoreListings(false);
    }
  }, [searchInput, categoryFilter, areaListings, sortBy]);

  useEffect(() => { fetchFeatured(); }, [fetchFeatured]);
  useEffect(() => { fetchListings(1, false); }, [fetchListings]);

  const applyFilterStrip = () => {
    setAreaListings(areaFilter);
    setSearchInput((prev) => prev);
    fetchListings(1, false);
  };

  const loadMoreListings = () => {
    if (loadingMoreListings || !listingsNextPage) return;
    fetchListings(listingsPage + 1, true);
  };

  const cityOptions = ['All Areas', 'Lahore', 'Karachi', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar'];

  return (
    <PageLayout>
      <Helmet>
        <title>Businesses Directory - PakPoints</title>
        <meta name="description" content="Discover verified businesses, restaurants, clinics, and services across Pakistan on PakPoints." />
      </Helmet>

      <div className="bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-10 gap-6 flex flex-col">

          {/* 1) FILTER STRIP */}
          <section className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              placeholder="Search business name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[140px] rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 min-w-[120px]"
            >
              {cityOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button type="button" onClick={applyFilterStrip} className="rounded-xl bg-emerald-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap">
              Apply Filters →
            </button>
          </section>

          {/* 2) FEATURED BUSINESSES */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <span>⭐</span> Featured Businesses
            </h2>
            {loadingFeatured ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" /></div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 md:overflow-visible md:flex-wrap md:max-w-none">
                {featured.length === 0 ? (
                  <p className="text-gray-500 text-sm">No featured businesses right now.</p>
                ) : (
                  featured.map((b) => <BusinessCardFeatured key={b.id} business={b} onViewDetails={handleViewDetails} />)
                )}
              </div>
            )}
          </section>

          {/* 3) BUSINESS LISTINGS */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Business Listings</h2>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
              >
                <option>All Categories</option>
                <option value="restaurant">Restaurant</option>
                <option value="clinic">Clinic</option>
                <option value="gym">Gym</option>
                <option value="salon">Salon</option>
                <option value="school">Academy</option>
                <option value="company">Software House</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="shop">Electronics</option>
                <option value="cafe">Cafe</option>
                <option value="bank">Bank</option>
                <option value="property_dealer">Property Dealer</option>
                <option value="other">Other</option>
              </select>
              <select
                value={areaListings}
                onChange={(e) => setAreaListings(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
              >
                {cityOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
              >
                <option>Sort by: Most Popular</option>
                <option>Sort by: Newest</option>
                <option>Sort by: Name</option>
              </select>
              <button type="button" onClick={() => fetchListings(1, false)} className="rounded-lg bg-gray-100 text-gray-700 px-3 py-2 text-sm font-medium hover:bg-gray-200">
                Apply Filters
              </button>
            </div>
            {listingsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">{listingsError}</div>
            )}
            {loadingListings ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {listings.length === 0 ? (
                    <p className="col-span-full text-gray-500 text-center py-8">No businesses match your filters.</p>
                  ) : (
                    listings.map((b) => <BusinessCardSmall key={b.id} business={b} onViewDetails={handleViewDetails} />)
                  )}
                </div>
                {listingsNextPage && listings.length > 0 && (
                  <div className="flex justify-center mt-6">
                    <button
                      type="button"
                      onClick={loadMoreListings}
                      disabled={loadingMoreListings}
                      className="rounded-xl bg-emerald-600 text-white px-6 py-3 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loadingMoreListings ? 'Loading…' : `Load more (${listings.length} of ${Math.max(listingsCount, listings.length)})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* 4) CTA FOR BUSINESS OWNERS */}
          <section className="rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Own a Business?</h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              List your business on PakPoints and reach thousands of customers.
            </p>
            <button
              type="button"
              onClick={() => navigate('/businesses/create')}
              className="rounded-xl bg-emerald-600 text-white px-6 py-3 font-medium hover:bg-emerald-700 transition-colors"
            >
              Add Your Business
            </button>
            <button type="button" onClick={() => navigate('/about')} className="ml-3 text-emerald-600 font-medium hover:underline">
              Learn More
            </button>
          </section>

        </div>
      </div>
    </PageLayout>
  );
}
